import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronRight, History, Loader2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { API, getAuthHeader } from '../../App';
import { AdjustmentRowActions } from './AdjustmentRowActions';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const monthLabel = (k) => { const [y, m] = (k || '').split('-'); return MONTHS[Number(m) - 1] ? `${MONTHS[Number(m) - 1]} ${y}` : k; };
const aed = (n) => (n === null || n === undefined ? '-' : Number(n).toLocaleString('en-AE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const fmtDate = (s) => (s ? new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-');

const HEADS = ['UID', 'Rider', 'Platform', 'Existing Gross Salary', 'Post Payroll Adjustment', 'Revised Gross Salary',
  'Existing Net Salary', 'Revised Net Salary', 'Reason of Adjustment', 'Date', 'Created by', ''];
const NUMERIC = new Set([3, 4, 5, 6, 7]);

const SummaryBox = ({ tone, label, value, testid }) => (
  <div className={`rounded-lg border p-3 ${tone.box}`} data-testid={testid}>
    <div className={`text-xs uppercase tracking-wide ${tone.label}`}>{label}</div>
    <div className={`text-xl font-bold ${tone.value}`}>{value}</div>
  </div>
);

const EditTrail = ({ r }) => (
  <>
    {(r.edit_history || []).filter((h) => h.action === 'edited').map((h) => (
      <div key={h.at} className="text-[10px] text-amber-700">
        Edited by {h.by} on {fmtDate(h.at)} - was {h.direction === 'credit' ? '+' : '-'}AED {aed(h.amount)}{h.category_label ? ` (${h.category_label})` : ''}: {h.reason}
      </div>
    ))}
    {r.status === 'deleted' && <div className="text-[10px] font-semibold text-rose-600">Deleted by {r.deleted_by} on {fmtDate(r.deleted_at)} - payroll and slip restored</div>}
  </>
);

const AdjustmentRow = ({ r, onChanged }) => (
  <tr className={`border-t border-slate-100 align-top hover:bg-slate-50/60 ${r.status === 'deleted' ? 'bg-slate-50 text-slate-400 [&>td:nth-child(-n+8)]:line-through' : ''}`} data-testid={`adj-history-row-${r.id}`}>
    <td className="px-2 py-1.5 font-mono text-blue-700 whitespace-nowrap">{r.uid || '-'}</td>
    <td className="px-2 py-1.5">{r.rider_name}</td>
    <td className="px-2 py-1.5">{r.platform || '-'}</td>
    <td className="px-2 py-1.5 text-right">{aed(r.gross_before)}</td>
    <td className={`px-2 py-1.5 text-right font-semibold whitespace-nowrap ${r.direction === 'credit' ? 'text-emerald-700' : 'text-rose-700'}`}>
      {r.direction === 'credit' ? '+' : '-'}{aed(r.amount)}
      <div className="text-[10px] font-normal">{r.direction === 'credit' ? 'Credit' : 'Debit'}</div>
    </td>
    <td className="px-2 py-1.5 text-right font-semibold">{aed(r.gross_after)}</td>
    <td className="px-2 py-1.5 text-right">{aed(r.net_before)}</td>
    <td className="px-2 py-1.5 text-right font-semibold">{aed(r.net_after)}</td>
    <td className="px-2 py-1.5 min-w-[220px]">
      <div className="font-medium text-slate-700">{r.detail}</div>
      {r.reason && <div className="text-slate-500">{r.reason}</div>}
      <EditTrail r={r} />
    </td>
    <td className="px-2 py-1.5 whitespace-nowrap">{fmtDate(r.date)}</td>
    <td className="px-2 py-1.5 text-slate-500 break-all">{r.created_by || '-'}</td>
    <td className="px-1 py-1"><AdjustmentRowActions r={r} onChanged={onChanged} /></td>
  </tr>
);

const MonthBlock = ({ m, defaultOpen, onChanged }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-lg border bg-white" data-testid={`adj-history-month-${m.month_key}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-left hover:bg-slate-50" data-testid={`adj-history-toggle-${m.month_key}`}>
        {open ? <ChevronDown className="w-4 h-4 text-slate-500" /> : <ChevronRight className="w-4 h-4 text-slate-500" />}
        <span className="font-semibold text-slate-800">{m.label}</span>
        <span className="text-xs text-slate-500">{m.count} adjustment{m.count === 1 ? '' : 's'} · {m.unique_riders} rider{m.unique_riders === 1 ? '' : 's'}{m.deleted_count ? ` · ${m.deleted_count} deleted` : ''}</span>
        <span className="text-xs text-emerald-700">Credits AED {aed(m.total_credit)}</span>
        <span className="text-xs text-rose-700">Debits AED {aed(m.total_debit)}</span>
        <span className="text-xs text-slate-600">Gross change {m.gross_change >= 0 ? '+' : ''}{aed(m.gross_change)} · Net change {m.net_change >= 0 ? '+' : ''}{aed(m.net_change)}</span>
      </button>
      {open && (
        <div className="overflow-x-auto border-t">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-slate-600">
              <tr>{HEADS.map((h, i) => <th key={h} className={`px-2 py-1.5 font-semibold whitespace-normal leading-tight max-w-[90px] ${NUMERIC.has(i) ? 'text-right' : 'text-left'}`}>{h}</th>)}</tr>
            </thead>
            <tbody>{m.rows.map((r) => <AdjustmentRow key={r.id} r={r} onChanged={onChanged} />)}</tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export const AdjustmentHistory = ({ refreshKey, children }) => {
  const [month, setMonth] = useState('all');
  const [data, setData] = useState(null);
  const [allMonths, setAllMonths] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/talent/payroll-adjustments/history`, { headers: getAuthHeader(), params: month === 'all' ? {} : { month } });
      setData(res.data);
      if (res.data.available_months) setAllMonths(res.data.available_months.filter((k) => /^\d{4}-\d{2}$/.test(k)));
    } catch (e) { setData({ months: [], summary: {} }); }
    setLoading(false);
  }, [month]);

  useEffect(() => { load(); }, [load, refreshKey]);
  const s = data?.summary || {};

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-slate-500">Showing totals for</span>
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="w-48 h-8 text-xs" data-testid="adj-summary-month-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All months</SelectItem>
            {allMonths.map((k) => <SelectItem key={k} value={k}>{monthLabel(k)}</SelectItem>)}
          </SelectContent>
        </Select>
        {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <SummaryBox testid="adjustments-pending-count" label="Pending" value={s.pending ?? 0} tone={{ box: 'bg-amber-50', label: 'text-amber-700', value: 'text-amber-800 text-2xl' }} />
        <SummaryBox testid="adjustments-credit-total" label="Total Credits (Refunds)" value={`AED ${aed(s.total_credit || 0)}`} tone={{ box: 'bg-emerald-50', label: 'text-emerald-700', value: 'text-emerald-800' }} />
        <SummaryBox testid="adjustments-debit-total" label="Total Debits" value={`AED ${aed(s.total_debit || 0)}`} tone={{ box: 'bg-rose-50', label: 'text-rose-700', value: 'text-rose-800' }} />
        <SummaryBox testid="adjustments-unique-riders" label="Unique Riders" value={s.unique_riders ?? 0} tone={{ box: 'bg-slate-50', label: 'text-slate-600', value: 'text-slate-800 text-2xl' }} />
      </div>
      {typeof children === 'function' ? children(month === 'all' || !data?.summary?.pending_ids ? null : new Set(data.summary.pending_ids)) : children}
      <div className="space-y-2" data-testid="adj-history">
        <div className="flex items-center gap-2 pt-2 font-semibold text-slate-800">
          <History className="w-4 h-4 text-slate-600" />Adjustment history by payroll month
        </div>
        {data && !data.months?.length && <div className="rounded-lg border border-dashed py-6 text-center text-xs text-slate-500" data-testid="adj-history-empty">No applied adjustments{month === 'all' ? '' : ` for ${monthLabel(month)}`} yet.</div>}
        {data?.months?.map((m, i) => <MonthBlock key={m.month_key} m={m} defaultOpen={i === 0} onChanged={load} />)}
      </div>
    </div>
  );
};
